import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGES = [
  { key: 'researching',    label: 'Researching',    color: '#6b7280' },
  { key: 'questionnaire',  label: 'Questionnaire',   color: '#f59e0b' },
  { key: 'contacted',      label: 'Contacted',       color: '#3b82f6' },
  { key: 'campus_visit',   label: 'Campus Visit',    color: '#f97316' },
  { key: 'official_visit', label: 'Official Visit',  color: '#8b5cf6' },
  { key: 'offer_received', label: 'Offer Received',  color: '#10b981' },
  { key: 'signed',         label: 'Committed',       color: '#d4af37' },
];
const INACTIVE_STAGES = ['declined', 'withdrawn'];
const RATINGS = ['A+','A','A-','B+','B','B-','C+','C','C-','D'];
const CATEGORIES = ['Reach', 'Target', 'Safety'];
const DIVISIONS = ['D1', 'D2', 'D3', 'NAIA'];
const SIZES = ['Small', 'Medium', 'Large'];
const CONTACT_TYPES = [
  { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' },
  { key: 'text', label: 'Text' }, { key: 'video', label: 'Video Call' },
  { key: 'in_person', label: 'In Person' }, { key: 'social', label: 'Social Media' },
];
const SPOKE_WITH_OPTS = [
  { key: 'head_coach', label: 'Head Coach' }, { key: 'asst_coach', label: 'Asst. Coach' },
  { key: 'both', label: 'Both' }, { key: 'other', label: 'Other' },
];
const SENTIMENTS = [
  { key: 'very_positive', emoji: '😄', label: 'Very Positive' },
  { key: 'positive',      emoji: '🙂', label: 'Positive' },
  { key: 'neutral',       emoji: '😐', label: 'Neutral' },
  { key: 'mixed',         emoji: '😕', label: 'Mixed' },
  { key: 'negative',      emoji: '😟', label: 'Negative' },
];

// Recruiting milestones in order
const MILESTONES = [
  { key: 'questionnaire_sent', label: 'Questionnaire', icon: '📋' },
  { key: 'coach_contacted',    label: 'Coach Contact',  icon: '📞' },
  { key: 'coach_responded',    label: 'Response',       icon: '✉️' },
  { key: 'visit',              label: 'Visit',          icon: '🏫' },
  { key: 'offer_received',     label: 'Offer',          icon: '💰' },
  { key: 'decision',           label: 'Decision',       icon: '🎓' },
];

// Stage → which milestones are done (order matches new stage order: questionnaire before contacted)
const STAGE_MILESTONES = {
  researching:    [],
  questionnaire:  ['questionnaire_sent'],
  contacted:      ['questionnaire_sent', 'coach_contacted', 'coach_responded'],
  campus_visit:   ['questionnaire_sent', 'coach_contacted', 'coach_responded', 'visit'],
  official_visit: ['questionnaire_sent', 'coach_contacted', 'coach_responded', 'visit'],
  offer_received: ['questionnaire_sent', 'coach_contacted', 'coach_responded', 'visit', 'offer_received'],
  signed:         ['questionnaire_sent', 'coach_contacted', 'coach_responded', 'visit', 'offer_received', 'decision'],
  declined:       ['questionnaire_sent', 'coach_contacted', 'coach_responded', 'visit', 'offer_received', 'decision'],
  withdrawn:      [],
};

const NCAA_ELIGIBILITY_OPTIONS = [
  { key: 'not_started', label: 'Not Started' },
  { key: 'registered',  label: 'Registered' },
  { key: 'amateurism',  label: 'Amateurism Certified' },
  { key: 'full',        label: 'Fully Certified' },
];

// Coach grading scorecard categories
const GRADE_CATEGORIES = [
  { key: 'athletic_program',  label: 'Athletic Program' },
  { key: 'coaching_staff',    label: 'Coaching Staff' },
  { key: 'academics',         label: 'Academics' },
  { key: 'financial_aid',     label: 'Financial Aid' },
  { key: 'campus_culture',    label: 'Campus Culture' },
  { key: 'location_fit',      label: 'Location / Fit' },
];

// Letter grade → numeric (for composite)
const GRADE_NUMS = { 'A+':4.3,'A':4.0,'A-':3.7,'B+':3.3,'B':3.0,'B-':2.7,'C+':2.3,'C':2.0,'C-':1.7,'D':1.0 };

function gradeToNum(g) { return GRADE_NUMS[g] ?? null; }
function numToGrade(n) {
  if (n >= 4.15) return 'A+';
  if (n >= 3.85) return 'A';
  if (n >= 3.5)  return 'A-';
  if (n >= 3.15) return 'B+';
  if (n >= 2.85) return 'B';
  if (n >= 2.5)  return 'B-';
  if (n >= 2.15) return 'C+';
  if (n >= 1.85) return 'C';
  if (n >= 1.5)  return 'C-';
  return 'D';
}
function computeOverall(grades) {
  const nums = Object.values(grades).map(gradeToNum).filter(Boolean);
  if (!nums.length) return null;
  return numToGrade(nums.reduce((a, b) => a + b, 0) / nums.length);
}

// Signing period countdowns — grade-level aware
// ESD = Nov 13, NSD = Feb 5 of the athlete's senior year
// Class year: Sept–June academic calendar
//   Jan–June: 12th = current year, 11th = current+1, etc.
//   Jul–Aug:  school year flipped — 12th = current+1, etc.
function getSigningCountdowns(gradeLevel) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-indexed

  const yearsUntilGrad = { '9th': 3, '10th': 2, '11th': 1, '12th': 0 }[gradeLevel] ?? 1;
  // After June (summer), the new school year has started — add 1
  const classYear = currentMonth >= 7
    ? currentYear + yearsUntilGrad + 1
    : currentYear + yearsUntilGrad;

  // ESD is Nov 13 of senior fall; NSD is Feb 5 of graduation spring
  const esdDate = new Date(classYear - 1, 10, 13); // Nov 13, year before graduation
  const nsdDate = new Date(classYear, 1, 5);         // Feb 5, graduation year

  const msPerDay = 1000 * 60 * 60 * 24;
  return {
    esd: Math.ceil((esdDate - now) / msPerDay),
    nsd: Math.ceil((nsdDate - now) / msPerDay),
    classYear,
    esdDate,
    nsdDate,
  };
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function formatDollars(n) {
  if (n == null || n === '') return '—';
  return `$${Number(n).toLocaleString()}`;
}
function sentimentEmoji(key) { return SENTIMENTS.find(s => s.key === key)?.emoji ?? ''; }
function formatSeconds(s) {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(2);
  return m > 0 ? `${m}:${sec.padStart(5, '0')}` : `${sec}s`;
}

// ─── Blank form defaults ──────────────────────────────────────────────────────

const BLANK_ADD = {
  name: '', state: '', division: 'D1', conference: '', school_size: 'Large',
  category: 'Target', head_coach: '', head_coach_email: '', asst_coach: '', asst_coach_email: '',
  roster_need: 'unknown', event_interest: '',
};
const BLANK_CONV = {
  conversation_date: new Date().toISOString().split('T')[0],
  contact_type: 'phone', spoke_with: 'head_coach', summary: '', next_steps: '', sentiment: 'positive',
};
const BLANK_VISIT = {
  visit_date: new Date().toISOString().split('T')[0], visit_type: 'unofficial', notes: '', impressions: 5,
};
const BLANK_OFFER = {
  athletic_amount_yr: '', academic_amount_yr: '', offer_date: new Date().toISOString().split('T')[0],
};
const BLANK_REQ = {
  request_date: new Date().toISOString().split('T')[0],
  requested_athletic_increase: '', requested_academic_increase: '', notes: '',
};
const BLANK_GRADES = Object.fromEntries(GRADE_CATEGORIES.map(c => [c.key, '']));

// ─── Email Templates ──────────────────────────────────────────────────────────

function buildTemplates(athleteName) {
  const name = athleteName || '[Your Name]';
  return [
    {
      title: 'Initial Coach Outreach',
      subject: `Class of [Year] / [Event] / ${name}`,
      body:
`Hi Coach [Last Name],

My name is ${name}. I'm a [Grade] at [High School] competing in [event] with a PR of [PR].

I've really enjoyed following your program and would love to be considered for your [Year] recruiting class. I'd be happy to send over my athletic resume and meet results if that would be helpful.

Thanks for your time.

${name}
[Phone] | [Email]`,
    },
    {
      title: 'Thank You After Campus Visit',
      subject: `Thank You / Campus Visit / ${name}`,
      body:
`Hi Coach [Last Name],

Just wanted to say thank you for having me on campus this past [weekend/week]. Getting to meet you, see the facility, and spend time with the team meant a lot.

It really solidified my interest. I left excited about the possibility of competing at [School Name] and studying [major/field].

Looking forward to staying in touch.

${name}`,
    },
    {
      title: 'Follow-Up / Check-In',
      subject: `Staying in Touch / ${name} / [Event]`,
      body:
`Hi Coach [Last Name],

I wanted to check in and let you know [School Name] is still high on my list. Things have been going well on my end. I recently [recent result] and am feeling good heading into [season / upcoming meet].

Whenever you have an update on your recruiting timeline I'd love to hear it. No rush at all.

Thanks Coach,

${name}`,
    },
    {
      title: 'Offer Acknowledgment',
      subject: `Re: [School Name] Offer / ${name}`,
      body:
`Hi Coach [Last Name],

Thank you so much, I really appreciate the offer. This is exciting news and I'm taking it seriously.

I'm working through my options and plan to have a decision by [date]. I'll be in touch soon.

${name}`,
    },
  ];
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function RecruitingView({ athleteId, athleteName }) {
  // Guard: if no athleteId, render nothing
  if (!athleteId) return null;

  return <RecruitingViewInner athleteId={athleteId} athleteName={athleteName} />;
}

function RecruitingViewInner({ athleteId, athleteName }) {
  const { profile } = useAuth();
  const [recruitProfile, setRecruitProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    sat_score: '', gpa: '', majors_interest: '', school_size_pref: '',
    video_link: '', ncaa_eligibility_status: 'not_started',
  });
  const [schools, setSchools] = useState([]);
  const [offersMap, setOffersMap] = useState({}); // school_id → offer row
  const [prs, setPrs] = useState([]);
  const [gradeLevel, setGradeLevel] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeSection, setActiveSection] = useState('pipeline'); // 'pipeline' | 'timeline' | 'resume' | 'compare' | 'templates'

  // Add school
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [addForm, setAddForm] = useState(BLANK_ADD);
  const [addSaving, setAddSaving] = useState(false);

  // School detail modal
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [schoolForm, setSchoolForm] = useState({});
  const [schoolSaving, setSchoolSaving] = useState(false);
  const [signConfirm, setSignConfirm] = useState(false);

  // Detail tab data
  const [conversations, setConversations] = useState([]);
  const [visits, setVisits] = useState([]);
  const [offer, setOffer] = useState(null);
  const [offerRequests, setOfferRequests] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Add forms
  const [showAddConv, setShowAddConv] = useState(false);
  const [convForm, setConvForm] = useState(BLANK_CONV);
  const [convSaving, setConvSaving] = useState(false);

  const [showAddVisit, setShowAddVisit] = useState(false);
  const [visitForm, setVisitForm] = useState(BLANK_VISIT);
  const [visitSaving, setVisitSaving] = useState(false);

  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerForm, setOfferForm] = useState(BLANK_OFFER);
  const [offerSaving, setOfferSaving] = useState(false);

  const [showReqForm, setShowReqForm] = useState(false);
  const [reqForm, setReqForm] = useState(BLANK_REQ);
  const [reqSaving, setReqSaving] = useState(false);

  // Grading
  const [grades, setGrades] = useState(BLANK_GRADES);
  const [gradeNotes, setGradeNotes] = useState('');
  const [gradeSaving, setGradeSaving] = useState(false);

  const [showInactive, setShowInactive] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Drag-and-drop state
  const [dragSchoolId, setDragSchoolId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  useEffect(() => {
    if (athleteId) loadData();
  }, [athleteId]);

  async function loadData() {
    setLoading(true);
    const [{ data: rp }, { data: sc }, { data: bm }, { data: ap }] = await Promise.all([
      supabase.from('recruiting_profiles').select('*').eq('athlete_id', athleteId).maybeSingle(),
      supabase.from('recruit_schools').select('*').eq('athlete_id', athleteId).order('created_at', { ascending: true }),
      supabase.from('benchmarks').select('event, time_seconds, race_date').eq('athlete_id', athleteId).order('race_date', { ascending: false }),
      supabase.from('athlete_profiles').select('grade_level').eq('id', athleteId).maybeSingle(),
    ]);
    setGradeLevel(ap?.grade_level ?? null);
    setRecruitProfile(rp ?? null);
    setProfileForm({
      sat_score: rp?.sat_score ?? '',
      gpa: rp?.gpa ?? '',
      majors_interest: rp?.majors_interest ?? '',
      school_size_pref: rp?.school_size_pref ?? '',
      video_link: rp?.video_link ?? '',
      ncaa_eligibility_status: rp?.ncaa_eligibility_status ?? 'not_started',
    });
    const schoolList = sc ?? [];
    setSchools(schoolList);

    // Load all offers for these schools in one query
    if (schoolList.length > 0) {
      const schoolIds = schoolList.map(s => s.id);
      const { data: allOffers } = await supabase
        .from('recruit_offers')
        .select('*')
        .in('school_id', schoolIds);
      const map = {};
      for (const o of (allOffers ?? [])) map[o.school_id] = o;
      setOffersMap(map);
    } else {
      setOffersMap({});
    }

    // Deduplicate best time per event
    const bestByEvent = {};
    for (const b of (bm ?? [])) {
      if (!bestByEvent[b.event] || b.time_seconds < bestByEvent[b.event].time_seconds) {
        bestByEvent[b.event] = b;
      }
    }
    setPrs(
      Object.entries(bestByEvent).map(([event, b]) => ({
        event,
        time_label: formatSeconds(b.time_seconds),
      }))
    );
    setLoading(false);
  }

  function handleDbError(label, error) {
    console.error(`[Recruiting] ${label}:`, error);
    setSaveError(`${label}: ${error.message}`);
  }

  async function saveProfile() {
    setSaveError(null);
    const payload = {
      athlete_id: athleteId,
      sat_score: profileForm.sat_score ? parseInt(profileForm.sat_score) : null,
      gpa: profileForm.gpa || null,
      majors_interest: profileForm.majors_interest || null,
      school_size_pref: profileForm.school_size_pref || null,
      video_link: profileForm.video_link || null,
      ncaa_eligibility_status: profileForm.ncaa_eligibility_status || null,
      updated_at: new Date().toISOString(),
    };
    let err;
    if (recruitProfile?.id) {
      ({ error: err } = await supabase.from('recruiting_profiles').update(payload).eq('id', recruitProfile.id));
    } else {
      ({ error: err } = await supabase.from('recruiting_profiles').insert(payload));
    }
    if (err) { handleDbError('Save profile', err); return; }
    setEditingProfile(false);
    loadData();
  }

  async function addSchool() {
    setSaveError(null);
    setAddSaving(true);
    const { error: err } = await supabase.from('recruit_schools').insert({
      athlete_id: athleteId,
      ...addForm,
      stage: 'researching',
    });
    setAddSaving(false);
    if (err) { handleDbError('Add school', err); return; }
    setShowAddSchool(false);
    setAddForm(BLANK_ADD);
    loadData();
  }

  const updateSchool = useCallback(async (updates) => {
    const { error: err } = await supabase.from('recruit_schools')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', selectedSchool.id);
    if (err) { handleDbError('Update school', err); return; }
    const updated = { ...selectedSchool, ...updates };
    setSelectedSchool(updated);
    setSchools(prev => prev.map(s => s.id === updated.id ? updated : s));
  }, [selectedSchool]);

  async function moveSchoolToStage(schoolId, newStage) {
    const school = schools.find(s => s.id === schoolId);
    if (!school || school.stage === newStage) return;
    // Optimistic update
    setSchools(prev => prev.map(s => s.id === schoolId ? { ...s, stage: newStage } : s));
    const { error: err } = await supabase.from('recruit_schools')
      .update({ stage: newStage, updated_at: new Date().toISOString() })
      .eq('id', schoolId);
    if (err) {
      handleDbError('Move school', err);
      setSchools(prev => prev.map(s => s.id === schoolId ? { ...s, stage: school.stage } : s));
    }
  }

  async function deleteSchool(schoolId) {
    if (!window.confirm('Remove this school from the list?')) return;
    const { error: err } = await supabase.from('recruit_schools').delete().eq('id', schoolId);
    if (err) { handleDbError('Delete school', err); return; }
    setSelectedSchool(null);
    setSchools(prev => prev.filter(s => s.id !== schoolId));
  }

  async function openSchoolModal(school) {
    setSelectedSchool(school);
    setSchoolForm({
      name: school.name, state: school.state ?? '', division: school.division ?? 'D1',
      conference: school.conference ?? '', school_size: school.school_size ?? '',
      category: school.category ?? 'Target', head_coach: school.head_coach ?? '',
      head_coach_email: school.head_coach_email ?? '', asst_coach: school.asst_coach ?? '',
      asst_coach_email: school.asst_coach_email ?? '', apex_rating: school.apex_rating ?? '',
      notes: school.notes ?? '', roster_need: school.roster_need ?? 'unknown',
      event_interest: school.event_interest ?? '',
    });
    setActiveTab('details');
    setSignConfirm(false);
    setShowAddConv(false);
    setShowAddVisit(false);
    setShowOfferForm(false);
    setShowReqForm(false);
    setGrades(BLANK_GRADES);
    setGradeNotes('');
    setDetailLoading(true);

    const [{ data: convs }, { data: vis }, { data: ofr }, { data: reqs }, { data: gr }] = await Promise.all([
      supabase.from('recruit_conversations').select('*').eq('school_id', school.id).eq('athlete_id', athleteId).order('conversation_date', { ascending: false }),
      supabase.from('recruit_visits').select('*').eq('school_id', school.id).eq('athlete_id', athleteId).order('visit_date', { ascending: false }),
      supabase.from('recruit_offers').select('*').eq('school_id', school.id).maybeSingle(),
      supabase.from('recruit_offer_requests').select('*').eq('school_id', school.id).eq('athlete_id', athleteId).order('request_date', { ascending: false }),
      supabase.from('recruit_school_grades').select('*').eq('school_id', school.id).eq('athlete_id', athleteId).maybeSingle(),
    ]);

    setConversations(convs ?? []);
    setVisits(vis ?? []);
    setOffer(ofr ?? null);
    setOfferRequests(reqs ?? []);
    if (gr) {
      const g = {};
      GRADE_CATEGORIES.forEach(c => { g[c.key] = gr[c.key] ?? ''; });
      setGrades(g);
      setGradeNotes(gr.notes ?? '');
    }
    setDetailLoading(false);
  }

  async function saveSchoolDetails() {
    setSaveError(null);
    setSchoolSaving(true);
    await updateSchool(schoolForm);
    setSchoolSaving(false);
  }

  async function saveGrades() {
    setSaveError(null);
    setGradeSaving(true);
    const overall = computeOverall(grades);
    const payload = {
      school_id: selectedSchool.id,
      athlete_id: athleteId,
      ...grades,
      overall_grade: overall,
      notes: gradeNotes || null,
      updated_at: new Date().toISOString(),
    };
    const { data: existing } = await supabase
      .from('recruit_school_grades')
      .select('id')
      .eq('school_id', selectedSchool.id)
      .eq('athlete_id', athleteId)
      .maybeSingle();
    let err;
    if (existing?.id) {
      ({ error: err } = await supabase.from('recruit_school_grades').update(payload).eq('id', existing.id));
    } else {
      ({ error: err } = await supabase.from('recruit_school_grades').insert(payload));
    }
    if (err) { handleDbError('Save grades', err); setGradeSaving(false); return; }
    if (overall) await updateSchool({ apex_rating: overall });
    setGradeSaving(false);
  }

  async function addConversation() {
    setSaveError(null);
    setConvSaving(true);
    const { error: err } = await supabase.from('recruit_conversations').insert({
      school_id: selectedSchool.id,
      athlete_id: athleteId,
      logged_by: profile.id,
      ...convForm,
    });
    if (err) { handleDbError('Add conversation', err); setConvSaving(false); return; }
    await supabase.from('recruit_schools')
      .update({ last_contact_date: convForm.conversation_date, updated_at: new Date().toISOString() })
      .eq('id', selectedSchool.id);
    const { data: convs } = await supabase.from('recruit_conversations').select('*')
      .eq('school_id', selectedSchool.id).eq('athlete_id', athleteId).order('conversation_date', { ascending: false });
    setConversations(convs ?? []);
    setSchools(prev => prev.map(s => s.id === selectedSchool.id ? { ...s, last_contact_date: convForm.conversation_date } : s));
    setConvForm(BLANK_CONV);
    setShowAddConv(false);
    setConvSaving(false);
    if (selectedSchool.stage === 'researching' || selectedSchool.stage === 'questionnaire') await updateSchool({ stage: 'contacted' });
  }

  async function addVisit() {
    setSaveError(null);
    setVisitSaving(true);
    const { error: err } = await supabase.from('recruit_visits').insert({
      school_id: selectedSchool.id,
      athlete_id: athleteId,
      ...visitForm,
    });
    if (err) { handleDbError('Add visit', err); setVisitSaving(false); return; }
    const { data: vis } = await supabase.from('recruit_visits').select('*')
      .eq('school_id', selectedSchool.id).eq('athlete_id', athleteId).order('visit_date', { ascending: false });
    setVisits(vis ?? []);
    setVisitForm(BLANK_VISIT);
    setShowAddVisit(false);
    setVisitSaving(false);
    const newStage = visitForm.visit_type === 'official' ? 'official_visit' : 'campus_visit';
    const stageOrder = STAGES.map(s => s.key);
    if (stageOrder.indexOf(selectedSchool.stage) < stageOrder.indexOf(newStage)) {
      await updateSchool({ stage: newStage });
    }
  }

  async function saveOffer() {
    setSaveError(null);
    setOfferSaving(true);
    const payload = {
      school_id: selectedSchool.id,
      athlete_id: athleteId,
      athletic_amount_yr: offerForm.athletic_amount_yr ? parseFloat(offerForm.athletic_amount_yr) : null,
      academic_amount_yr: offerForm.academic_amount_yr ? parseFloat(offerForm.academic_amount_yr) : null,
      offer_date: offerForm.offer_date || null,
      status: 'pending',
      updated_at: new Date().toISOString(),
    };
    let err;
    if (offer?.id) {
      ({ error: err } = await supabase.from('recruit_offers').update(payload).eq('id', offer.id));
    } else {
      ({ error: err } = await supabase.from('recruit_offers').insert(payload));
    }
    if (err) { handleDbError('Save offer', err); setOfferSaving(false); return; }
    const { data: ofr } = await supabase.from('recruit_offers').select('*').eq('school_id', selectedSchool.id).maybeSingle();
    setOffer(ofr ?? null);
    if (ofr) setOffersMap(prev => ({ ...prev, [selectedSchool.id]: ofr }));
    setShowOfferForm(false);
    setOfferSaving(false);
    const currentIdx = STAGES.findIndex(s => s.key === selectedSchool.stage);
    const offerIdx = STAGES.findIndex(s => s.key === 'offer_received');
    if (currentIdx < offerIdx) await updateSchool({ stage: 'offer_received' });
  }

  async function addOfferRequest() {
    setSaveError(null);
    setReqSaving(true);
    const { error: err } = await supabase.from('recruit_offer_requests').insert({
      school_id: selectedSchool.id,
      athlete_id: athleteId,
      requested_athletic_increase: reqForm.requested_athletic_increase ? parseFloat(reqForm.requested_athletic_increase) : null,
      requested_academic_increase: reqForm.requested_academic_increase ? parseFloat(reqForm.requested_academic_increase) : null,
      request_date: reqForm.request_date,
      notes: reqForm.notes || null,
      school_response: 'pending',
    });
    if (err) { handleDbError('Add financial aid request', err); setReqSaving(false); return; }
    const { data: reqs } = await supabase.from('recruit_offer_requests').select('*')
      .eq('school_id', selectedSchool.id).eq('athlete_id', athleteId).order('request_date', { ascending: false });
    setOfferRequests(reqs ?? []);
    setReqForm(BLANK_REQ);
    setShowReqForm(false);
    setReqSaving(false);
  }

  async function updateRequestResponse(reqId, response, newAthletic, newAcademic) {
    const { error: err } = await supabase.from('recruit_offer_requests').update({
      school_response: response,
      new_athletic_amount: newAthletic ? parseFloat(newAthletic) : null,
      new_academic_amount: newAcademic ? parseFloat(newAcademic) : null,
      response_date: new Date().toISOString().split('T')[0],
    }).eq('id', reqId);
    if (err) { handleDbError('Update response', err); return; }
    const { data: reqs } = await supabase.from('recruit_offer_requests').select('*')
      .eq('school_id', selectedSchool.id).eq('athlete_id', athleteId).order('request_date', { ascending: false });
    setOfferRequests(reqs ?? []);
  }

  function copyTemplate(text, idx) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  }

  // ── Computed ────────────────────────────────────────────────────────────────
  const countdowns = getSigningCountdowns(gradeLevel);
  const activeSchools = schools.filter(s => !INACTIVE_STAGES.includes(s.stage));
  const inactiveSchools = schools.filter(s => INACTIVE_STAGES.includes(s.stage));
  const schoolsWithOffers = schools.filter(s => s.stage === 'offer_received' || s.stage === 'signed');
  const committedSchool = schools.find(s => s.stage === 'signed');
  const officialVisitsUsed = schools.filter(s =>
    s.stage === 'official_visit' || s.stage === 'offer_received' || s.stage === 'signed'
  ).length;
  const overallGrade = computeOverall(grades);
  const templates = buildTemplates(athleteName);

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="loading-screen" style={{ minHeight: 200 }}>
        <div className="loading-dot-row">
          <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Error banner */}
      {saveError && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#991b1b' }}>
          <span><strong>Save failed:</strong> {saveError}</span>
          <button onClick={() => setSaveError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, color: '#991b1b', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}
      {/* ── Section nav ── */}
      <div className="rec-nav-bar">
        <div className="rec-section-tabs">
          {[
            { key: 'pipeline',  label: 'Pipeline' },
            { key: 'timeline',  label: 'Timeline' },
            { key: 'resume',    label: 'Resume' },
            { key: 'compare',   label: 'Compare' },
            { key: 'templates', label: 'Templates' },
          ].map(v => (
            <button
              key={v.key}
              className={`rec-page-tab${activeSection === v.key ? ' rec-page-tab--active' : ''}`}
              onClick={() => setActiveSection(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap', fontWeight: 600 }} onClick={() => setShowAddSchool(true)}>
          + Add School
        </button>
      </div>

      {/* ── Committed banner ── */}
      {committedSchool && (
        <div className="committed-banner">
          <div className="committed-banner-emoji">🎉</div>
          <div className="committed-banner-text">
            <div className="committed-banner-title">Committed to {committedSchool.name}!</div>
            <div className="committed-banner-sub">{[committedSchool.division, committedSchool.conference].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
      )}

      {/* ── Status strip: stats + visits + countdowns in one row ── */}
      <div className="rec-status-strip">
        <div className="rec-strip-stat">
          <div className="rec-strip-stat-value">{activeSchools.length}</div>
          <div className="rec-strip-stat-label">Schools</div>
        </div>
        <div className="rec-strip-divider" />
        <div className="rec-strip-stat">
          <div className="rec-strip-stat-value">{schoolsWithOffers.length}</div>
          <div className="rec-strip-stat-label">Offers</div>
        </div>
        <div className="rec-strip-divider" />
        <div>
          <div className="rec-strip-stat-label" style={{ marginBottom: 5 }}>Official Visits</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className={`rec-visit-pip${i < officialVisitsUsed ? ' rec-visit-pip--used' : ''}`} />
            ))}
            <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 3 }}>{officialVisitsUsed}/5</span>
          </div>
        </div>
        {gradeLevel && (
          <>
            <div className="rec-strip-divider" />
            <div>
              <div className="rec-strip-stat-label" style={{ marginBottom: 5 }}>Class of {countdowns.classYear}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <span className={`rec-inline-chip${countdowns.esd <= 0 ? ' rec-inline-chip--muted' : ' rec-inline-chip--blue'}`}>
                  {countdowns.esd > 0 ? `${countdowns.esd}d to ESD` : 'ESD passed'}
                </span>
                <span className={`rec-inline-chip${countdowns.nsd <= 0 ? ' rec-inline-chip--muted' : ' rec-inline-chip--purple'}`}>
                  {countdowns.nsd > 0 ? `${countdowns.nsd}d to NSD` : 'NSD passed'}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Section: Pipeline ── */}
      {activeSection === 'pipeline' && (
        <>
          {activeSchools.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">No schools added yet</p>
              <p className="empty-state-sub">Start building your list by clicking "Add School".</p>
            </div>
          ) : (
            <div className="section-card" style={{ marginBottom: 20 }}>
              <div className="section-card-header">
                <span className="section-card-title">Recruiting Pipeline</span>
                <span className="cell-muted" style={{ fontSize: 12 }}>Click a school to manage</span>
              </div>
              <div className="section-card-body" style={{ overflowX: 'auto', padding: '4px 0 8px' }}>
                <div className="pipeline-scroll">
                  {STAGES.map(stage => {
                    const stageSchools = activeSchools.filter(s => s.stage === stage.key);
                    return (
                      <div
                        key={stage.key}
                        className={`pipeline-col${dragOverStage === stage.key ? ' pipeline-col--dragover' : ''}`}
                        onDragOver={e => { e.preventDefault(); setDragOverStage(stage.key); }}
                        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverStage(null); }}
                        onDrop={e => { e.preventDefault(); setDragOverStage(null); if (dragSchoolId) { moveSchoolToStage(dragSchoolId, stage.key); setDragSchoolId(null); } }}
                      >
                        <div className="pipeline-col-header" style={{ background: stage.color }}>
                          <span className="pipeline-col-label">{stage.label}</span>
                          <span className="pipeline-col-count">{stageSchools.length}</span>
                        </div>
                        <div className="pipeline-col-cards">
                          {stageSchools.map(school => (
                            <div
                              key={school.id}
                              className={`school-card${dragSchoolId === school.id ? ' school-card--dragging' : ''}`}
                              draggable
                              onDragStart={e => { setDragSchoolId(school.id); e.dataTransfer.effectAllowed = 'move'; }}
                              onDragEnd={() => { setDragSchoolId(null); setDragOverStage(null); }}
                              onClick={() => openSchoolModal(school)}
                            >
                              <div className="school-card-name">{school.name}</div>
                              <div className="school-card-sub">
                                {[school.state, school.conference].filter(Boolean).join(' · ') || '—'}
                              </div>
                              <div className="school-card-badges">
                                {school.division && <span className="div-badge">{school.division}</span>}
                                {school.category && <span className={`category-badge category-badge--${school.category.toLowerCase()}`}>{school.category}</span>}
                                {school.apex_rating && <span className="apex-rating-badge">{school.apex_rating}</span>}
                                {school.is_top5 && <span className="top5-star">★</span>}
                                {school.roster_need === 'yes' && <span className="roster-need-tag roster-need-tag--yes">Need</span>}
                                {school.roster_need === 'no' && <span className="roster-need-tag roster-need-tag--no">Full</span>}
                              </div>
                              {school.last_contact_date && (
                                <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 5 }}>
                                  Last: {formatDate(school.last_contact_date)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Inactive schools */}
          {inactiveSchools.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <button
                className="btn btn-secondary"
                style={{ fontSize: 12 }}
                onClick={() => setShowInactive(p => !p)}
              >
                {showInactive ? 'Hide' : 'Show'} Removed Schools ({inactiveSchools.length})
              </button>
              {showInactive && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8, marginTop: 10 }}>
                  {inactiveSchools.map(school => (
                    <div key={school.id} className="school-card school-card--faded" onClick={() => openSchoolModal(school)}>
                      <div className="school-card-name">{school.name}</div>
                      <div className="school-card-sub">{school.state ?? '—'}</div>
                      <div className="school-card-badges">
                        {school.category && <span className={`category-badge category-badge--${school.category.toLowerCase()}`}>{school.category}</span>}
                        <span style={{ fontSize: 10, background: '#f3f4f6', color: '#6b7280', borderRadius: 4, padding: '1px 6px', fontWeight: 600, textTransform: 'uppercase' }}>
                          {school.stage}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Section: Timeline ── */}
      {activeSection === 'timeline' && (
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">Recruiting Timeline</span>
            <span className="cell-muted" style={{ fontSize: 12 }}>Click a school for details</span>
          </div>
          <div className="section-card-body">
            {activeSchools.length === 0 ? (
              <p style={{ color: 'var(--gray-400)', fontSize: 13, textAlign: 'center', padding: 24 }}>No schools added yet.</p>
            ) : (
              <div className="rec-timeline">
                {activeSchools.map(school => {
                  const doneMilestones = STAGE_MILESTONES[school.stage] ?? [];
                  const activeMilestone = MILESTONES.find(m => !doneMilestones.includes(m.key));
                  return (
                    <div key={school.id} className="rec-timeline-school">
                      <div className="rec-timeline-school-header" onClick={() => openSchoolModal(school)}>
                        <span className="rec-timeline-school-name">{school.name}</span>
                        {school.state && <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{school.state}</span>}
                        {school.division && <span className="div-badge">{school.division}</span>}
                        {school.category && <span className={`category-badge category-badge--${school.category.toLowerCase()}`}>{school.category}</span>}
                        {school.apex_rating && <span className="apex-rating-badge">{school.apex_rating}</span>}
                        {school.roster_need === 'yes' && <span className="roster-need-tag roster-need-tag--yes">Need</span>}
                        {school.roster_need === 'no' && <span className="roster-need-tag roster-need-tag--no">Full</span>}
                        <span style={{ fontSize: 11, color: 'var(--gray-400)', marginLeft: 4 }}>→</span>
                      </div>
                      <div className="rec-timeline-body">
                        <div className="rec-milestones">
                          {MILESTONES.map(m => {
                            const isDone = doneMilestones.includes(m.key);
                            const isActive = activeMilestone?.key === m.key;
                            const isDeclined = school.stage === 'declined' && m.key === 'decision';
                            const isSigned = school.stage === 'signed' && m.key === 'decision';
                            return (
                              <div
                                key={m.key}
                                className={`rec-milestone${isDone ? ' rec-milestone--done' : ''}${isActive ? ' rec-milestone--active' : ''}${isDeclined ? ' rec-milestone--declined' : ''}${isSigned ? ' rec-milestone--signed' : ''}`}
                              >
                                <div className="rec-milestone-dot">{isDone ? '✓' : isActive ? m.icon : ''}</div>
                                <div className="rec-milestone-label">{m.label}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Section: Resume ── */}
      {activeSection === 'resume' && (
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">Recruiting Profile</span>
            {!editingProfile && (
              <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setEditingProfile(true)}>Edit Profile</button>
            )}
          </div>
          <div className="section-card-body">
            {/* NCAA Eligibility tracker */}
            <div className="rec-ncaa-tracker">
              <span className="rec-ncaa-tracker-label">NCAA Eligibility Center</span>
              <div className="rec-ncaa-tracker-steps">
                {NCAA_ELIGIBILITY_OPTIONS.map((opt, idx) => {
                  const current = recruitProfile?.ncaa_eligibility_status ?? 'not_started';
                  const curIdx = NCAA_ELIGIBILITY_OPTIONS.findIndex(o => o.key === current);
                  const isDone = idx < curIdx;
                  const isActive = idx === curIdx;
                  return (
                    <div key={opt.key} className={`rec-ncaa-step${isDone ? ' rec-ncaa-step--done' : ''}${isActive ? ' rec-ncaa-step--active' : ''}`}>
                      <div className="rec-ncaa-step-dot">{isDone ? '✓' : idx + 1}</div>
                      <div className="rec-ncaa-step-label">{opt.label}</div>
                      {idx < NCAA_ELIGIBILITY_OPTIONS.length - 1 && <div className="rec-ncaa-step-line" />}
                    </div>
                  );
                })}
              </div>
            </div>
            {editingProfile ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">SAT Score</label>
                    <input className="form-input" type="number" min={400} max={1600} placeholder="e.g. 1200"
                      value={profileForm.sat_score} onChange={e => setProfileForm(p => ({ ...p, sat_score: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">GPA (Unweighted)</label>
                    <input className="form-input" type="number" step="0.01" min={0} max={4} placeholder="e.g. 3.8"
                      value={profileForm.gpa} onChange={e => setProfileForm(p => ({ ...p, gpa: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">School Size Preference</label>
                    <select className="form-input form-select" value={profileForm.school_size_pref}
                      onChange={e => setProfileForm(p => ({ ...p, school_size_pref: e.target.value }))}>
                      <option value="">Any</option>
                      <option>Small</option><option>Medium</option><option>Large</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">NCAA Eligibility Status</label>
                    <select className="form-input form-select" value={profileForm.ncaa_eligibility_status}
                      onChange={e => setProfileForm(p => ({ ...p, ncaa_eligibility_status: e.target.value }))}>
                      {NCAA_ELIGIBILITY_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Majors of Interest</label>
                    <input className="form-input" placeholder="e.g. Exercise Science, Business, Pre-Med"
                      value={profileForm.majors_interest} onChange={e => setProfileForm(p => ({ ...p, majors_interest: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 2' }}>
                    <label className="form-label">Highlight Film / Video Link</label>
                    <input className="form-input" placeholder="YouTube, Hudl, or Google Drive link"
                      value={profileForm.video_link} onChange={e => setProfileForm(p => ({ ...p, video_link: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={saveProfile}>Save</button>
                  <button className="btn btn-secondary" onClick={() => setEditingProfile(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="rec-resume-section" style={{ marginBottom: 16, padding: '14px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}>
                  <div className="rec-resume-section-title" style={{ color: '#92400e' }}>NCAA Eligibility Center</div>
                  <p style={{ fontSize: 13, color: '#78350f', margin: '0 0 8px' }}>
                    Complete your amateurism certification. Required for all Division I and Division II athletes.
                  </p>
                  <a
                    href="https://web3.ncaa.org/ecwr3/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '8px 18px', textDecoration: 'none' }}
                  >
                    NCAA Eligibility Center ↗
                  </a>
                </div>
                <div className="rec-resume-grid">
                  <div className="rec-resume-section" style={{ textAlign: 'center' }}>
                    <div className="rec-resume-section-title">Academic</div>
                    <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 700 }}>SAT</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)' }}>{recruitProfile?.sat_score ?? '—'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)', fontWeight: 700 }}>GPA</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)' }}>{recruitProfile?.gpa ?? '—'}</div>
                      </div>
                    </div>
                    {recruitProfile?.majors_interest && (
                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--gray-600)' }}>
                        <strong>Interests:</strong> {recruitProfile.majors_interest}
                      </div>
                    )}
                  </div>

                  <div className="rec-resume-section">
                    <div className="rec-resume-section-title">Preferences</div>
                    <div style={{ fontSize: 13, color: 'var(--gray-700)' }}>
                      <div><strong>Size:</strong> {recruitProfile?.school_size_pref ?? '—'}</div>
                      <div style={{ marginTop: 4 }}>
                        <strong>NCAA Status:</strong>{' '}
                        {NCAA_ELIGIBILITY_OPTIONS.find(o => o.key === (recruitProfile?.ncaa_eligibility_status ?? 'not_started'))?.label}
                      </div>
                    </div>
                    {recruitProfile?.video_link && (
                      <a href={recruitProfile.video_link} target="_blank" rel="noreferrer"
                        style={{ display: 'inline-block', marginTop: 8, fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                        🎬 View Highlight Film
                      </a>
                    )}
                  </div>

                  <div className="rec-resume-section rec-resume-section--full">
                    <div className="rec-resume-section-title">Personal Records</div>
                    {prs.length === 0 ? (
                      <p style={{ fontSize: 13, color: 'var(--gray-400)' }}>No benchmarks logged yet. Add race results to populate your PRs.</p>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
                        {prs.map(pr => (
                          <div key={pr.event} className="rec-resume-pr-row">
                            <span className="rec-resume-pr-event">{pr.event}</span>
                            <span className="rec-resume-pr-time">{pr.time_label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Section: Compare ── */}
      {activeSection === 'compare' && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 14 }}>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', margin: 0 }}>Offer Comparison</h2>
            <p style={{ fontSize: 12, color: 'var(--gray-400)', margin: '4px 0 0' }}>
              Schools at Offer Received or Committed stage. Log scholarship amounts in each school's Scholarship tab.
            </p>
          </div>
          {schoolsWithOffers.length === 0 ? (
            <div className="empty-state">
              <p className="empty-state-title">No offers yet</p>
              <p className="empty-state-sub">Once a school reaches the "Offer Received" stage, it'll appear here for comparison.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {schoolsWithOffers.map(school => {
                const ofr = offersMap[school.id];
                const athletic = ofr?.athletic_amount_yr ?? null;
                const academic = ofr?.academic_amount_yr ?? null;
                const total = (athletic ?? 0) + (academic ?? 0);
                const hasNumbers = athletic != null || academic != null;
                const isCommitted = school.stage === 'signed';
                return (
                  <div key={school.id} style={{
                    background: 'var(--white)',
                    border: `2px solid ${isCommitted ? '#d4af37' : 'var(--gray-200)'}`,
                    borderRadius: 12,
                    overflow: 'hidden',
                  }}>
                    {/* School header */}
                    <div style={{
                      background: isCommitted ? '#fefce8' : 'var(--gray-50)',
                      padding: '12px 16px',
                      borderBottom: `1px solid ${isCommitted ? '#fde68a' : 'var(--gray-100)'}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--navy)', flex: 1 }}>{school.name}</span>
                        {isCommitted && <span style={{ fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fde68a', borderRadius: 4, padding: '2px 7px' }}>Committed</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 3 }}>
                        {[school.state, school.division, school.conference].filter(Boolean).join(' · ')}
                      </div>
                    </div>

                    {/* Financials */}
                    <div style={{ padding: '14px 16px' }}>
                      {hasNumbers ? (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                            <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '10px 12px' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Athletic / yr</div>
                              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--navy)' }}>{athletic != null ? formatDollars(athletic) : '—'}</div>
                            </div>
                            <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '10px 12px' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Academic / yr</div>
                              <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--navy)' }}>{academic != null ? formatDollars(academic) : '—'}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: '1px solid var(--gray-100)' }}>
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-400)' }}>Total / yr</div>
                              <div style={{ fontSize: 20, fontWeight: 900, color: isCommitted ? '#92400e' : 'var(--navy)' }}>{formatDollars(total)}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--gray-400)' }}>4-Year Total</div>
                              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--gray-500)' }}>{formatDollars(total * 4)}</div>
                            </div>
                          </div>
                          {ofr?.offer_date && (
                            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 8 }}>Offered {formatDate(ofr.offer_date)}</div>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--gray-400)', fontSize: 13 }}>
                          No scholarship amounts logged yet.
                          <br />
                          <span style={{ fontSize: 11 }}>Open this school → Scholarship tab to add.</span>
                        </div>
                      )}
                      {school.apex_rating && (
                        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>Overall Grade</span>
                          <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--navy)', background: 'var(--gray-100)', borderRadius: 6, padding: '2px 8px' }}>{school.apex_rating}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Section: Email Templates ── */}
      {activeSection === 'templates' && (
        <div className="section-card" style={{ marginBottom: 20 }}>
          <div className="section-card-header">
            <span className="section-card-title">Coach Email Templates</span>
            <span className="cell-muted" style={{ fontSize: 12 }}>Customize before sending</span>
          </div>
          <div className="section-card-body">
            <div className="rec-template-list">
              {templates.map((tpl, idx) => (
                <div key={idx} className="rec-template-card">
                  <div className="rec-template-header">
                    <span className="rec-template-title">{tpl.title}</span>
                    <button
                      className={`rec-template-copy-btn${copiedIdx === idx ? ' rec-template-copy-btn--copied' : ''}`}
                      onClick={() => copyTemplate(`Subject: ${tpl.subject}\n\n${tpl.body}`, idx)}
                    >
                      {copiedIdx === idx ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="rec-template-subject">Subject: {tpl.subject}</div>
                  <div className="rec-template-body">{tpl.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Add School Modal ── */}
      {showAddSchool && (
        <div className="tl-modal-overlay" onClick={() => setShowAddSchool(false)}>
          <div className="tl-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="tl-modal-header">
              <h2 className="tl-modal-title">Add School</h2>
              <button className="tl-modal-close" onClick={() => setShowAddSchool(false)}>×</button>
            </div>
            <div className="tl-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">School Name *</label>
                  <input className="form-input" placeholder="e.g. University of Colorado"
                    value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <input className="form-input" value={addForm.state} onChange={e => setAddForm(p => ({ ...p, state: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Division</label>
                  <select className="form-input form-select" value={addForm.division}
                    onChange={e => setAddForm(p => ({ ...p, division: e.target.value }))}>
                    {DIVISIONS.map(d => <option key={d}>{d}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Conference</label>
                  <input className="form-input" value={addForm.conference} onChange={e => setAddForm(p => ({ ...p, conference: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-input form-select" value={addForm.category}
                    onChange={e => setAddForm(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Head Coach Name</label>
                  <input className="form-input" value={addForm.head_coach} onChange={e => setAddForm(p => ({ ...p, head_coach: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Head Coach Email</label>
                  <input className="form-input" type="email" value={addForm.head_coach_email}
                    onChange={e => setAddForm(p => ({ ...p, head_coach_email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Asst. Coach Name</label>
                  <input className="form-input" value={addForm.asst_coach} onChange={e => setAddForm(p => ({ ...p, asst_coach: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Asst. Coach Email</label>
                  <input className="form-input" type="email" value={addForm.asst_coach_email}
                    onChange={e => setAddForm(p => ({ ...p, asst_coach_email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Roster Need</label>
                  <select className="form-input form-select" value={addForm.roster_need}
                    onChange={e => setAddForm(p => ({ ...p, roster_need: e.target.value }))}>
                    <option value="unknown">Unknown</option>
                    <option value="yes">Yes — need at my event</option>
                    <option value="no">No — full</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Event(s) of Interest</label>
                  <input className="form-input" placeholder="e.g. 1500m, Mile"
                    value={addForm.event_interest} onChange={e => setAddForm(p => ({ ...p, event_interest: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-primary" disabled={!addForm.name || addSaving} onClick={addSchool}>
                  {addSaving ? 'Adding…' : 'Add School'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowAddSchool(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── School Detail Modal ── */}
      {selectedSchool && (
        <div className="tl-modal-overlay" onClick={() => setSelectedSchool(null)}>
          <div className="tl-modal school-modal" onClick={e => e.stopPropagation()}>
            <div className="tl-modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                <div style={{ flex: 1 }}>
                  <h2 className="tl-modal-title" style={{ margin: 0 }}>{selectedSchool.name}</h2>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {selectedSchool.division && <span className="div-badge">{selectedSchool.division}</span>}
                    {selectedSchool.conference && <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{selectedSchool.conference}</span>}
                    {selectedSchool.category && <span className={`category-badge category-badge--${selectedSchool.category.toLowerCase()}`}>{selectedSchool.category}</span>}
                    {selectedSchool.apex_rating && <span className="apex-rating-badge">{selectedSchool.apex_rating}</span>}
                    {selectedSchool.is_top5 && <span className="top5-star">★ Top 5</span>}
                    {selectedSchool.roster_need && selectedSchool.roster_need !== 'unknown' && (
                      <span className={`roster-need-tag roster-need-tag--${selectedSchool.roster_need}`}>
                        {selectedSchool.roster_need === 'yes' ? 'Roster Need' : 'Full'}
                      </span>
                    )}
                  </div>
                </div>
                <button className="tl-modal-close" style={{ alignSelf: 'flex-start' }} onClick={() => setSelectedSchool(null)}>×</button>
              </div>

              {/* Milestone strip */}
              <div style={{ width: '100%', overflowX: 'auto', paddingBottom: 4 }}>
                <div className="rec-milestones" style={{ minWidth: 520 }}>
                  {MILESTONES.map(m => {
                    const doneMilestones = STAGE_MILESTONES[selectedSchool.stage] ?? [];
                    const isDone = doneMilestones.includes(m.key);
                    const activeMilestone = MILESTONES.find(ms => !doneMilestones.includes(ms.key));
                    const isActive = activeMilestone?.key === m.key;
                    const isDeclined = selectedSchool.stage === 'declined' && m.key === 'decision';
                    const isSigned = selectedSchool.stage === 'signed' && m.key === 'decision';
                    return (
                      <div key={m.key}
                        className={`rec-milestone${isDone ? ' rec-milestone--done' : ''}${isActive ? ' rec-milestone--active' : ''}${isDeclined ? ' rec-milestone--declined' : ''}${isSigned ? ' rec-milestone--signed' : ''}`}>
                        <div className="rec-milestone-dot">{isDone ? '✓' : isActive ? m.icon : ''}</div>
                        <div className="rec-milestone-label">{m.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Stage selector */}
              <div className="stage-selector">
                {[...STAGES, { key: 'declined', label: 'Declined', color: '#6b7280' }, { key: 'withdrawn', label: 'Withdrawn', color: '#9ca3af' }].map(s => (
                  <button
                    key={s.key}
                    className={`stage-pill${selectedSchool.stage === s.key ? ' stage-pill--active' : ''}`}
                    style={selectedSchool.stage === s.key ? { background: s.color } : {}}
                    onClick={() => {
                      if (s.key === 'signed') { setSignConfirm(true); return; }
                      updateSchool({ stage: s.key });
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              {signConfirm && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--gray-600)' }}>Confirm commitment to {selectedSchool.name}?</span>
                  <button className="btn btn-primary" style={{ fontSize: 11, background: '#d4af37', borderColor: '#d4af37' }}
                    onClick={() => { updateSchool({ stage: 'signed' }); setSignConfirm(false); }}>
                    Yes — Committed!
                  </button>
                  <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setSignConfirm(false)}>Cancel</button>
                </div>
              )}
            </div>

            <div className="tl-modal-body">
              <div className="modal-tabs">
                {['Details', 'Conversations', 'Visits', 'Scholarship', 'Grade'].map(tab => (
                  <button key={tab} className={`modal-tab${activeTab === tab.toLowerCase() ? ' modal-tab--active' : ''}`}
                    onClick={() => setActiveTab(tab.toLowerCase())}>
                    {tab}
                  </button>
                ))}
              </div>

              {detailLoading ? (
                <div className="loading-screen" style={{ minHeight: 100 }}>
                  <div className="loading-dot-row">
                    <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
                  </div>
                </div>
              ) : (
                <>
                  {/* ── Details tab ── */}
                  {activeTab === 'details' && (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {[
                          { label: 'School Name', key: 'name' },
                          { label: 'State', key: 'state' },
                          { label: 'Division', key: 'division', type: 'select', options: DIVISIONS },
                          { label: 'Conference', key: 'conference' },
                          { label: 'Size', key: 'school_size', type: 'select', options: SIZES },
                          { label: 'Category', key: 'category', type: 'select', options: CATEGORIES },
                          { label: 'Apex Rating', key: 'apex_rating', type: 'select', options: ['', ...RATINGS] },
                          { label: 'Roster Need', key: 'roster_need', type: 'select', options: [{ v: 'unknown', l: 'Unknown' }, { v: 'yes', l: 'Yes — Need' }, { v: 'no', l: 'No — Full' }] },
                          { label: 'Head Coach', key: 'head_coach' },
                          { label: 'HC Email', key: 'head_coach_email' },
                          { label: 'Asst. Coach', key: 'asst_coach' },
                          { label: 'Asst. Email', key: 'asst_coach_email' },
                          { label: 'Event(s) of Interest', key: 'event_interest' },
                        ].map(f => (
                          <div key={f.key} className="form-group">
                            <label className="form-label">{f.label}</label>
                            {f.type === 'select' ? (
                              <select className="form-input form-select" value={schoolForm[f.key] ?? ''}
                                onChange={e => setSchoolForm(p => ({ ...p, [f.key]: e.target.value }))}>
                                {(f.options ?? []).map(o =>
                                  typeof o === 'object'
                                    ? <option key={o.v} value={o.v}>{o.l}</option>
                                    : <option key={o} value={o}>{o || '—'}</option>
                                )}
                              </select>
                            ) : (
                              <input className="form-input" value={schoolForm[f.key] ?? ''}
                                onChange={e => setSchoolForm(p => ({ ...p, [f.key]: e.target.value }))} />
                            )}
                          </div>
                        ))}
                        <div className="form-group" style={{ gridColumn: 'span 2' }}>
                          <label className="form-label">Notes</label>
                          <textarea className="form-input" rows={3} style={{ resize: 'vertical' }}
                            value={schoolForm.notes ?? ''}
                            onChange={e => setSchoolForm(p => ({ ...p, notes: e.target.value }))} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                        <button className="btn btn-primary" disabled={schoolSaving} onClick={saveSchoolDetails}>
                          {schoolSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                        <button className="btn btn-secondary" style={{ color: '#b91c1c', borderColor: '#fecaca' }}
                          onClick={() => deleteSchool(selectedSchool.id)}>
                          Remove School
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Conversations tab ── */}
                  {activeTab === 'conversations' && (
                    <div>
                      <button className="btn btn-primary" style={{ fontSize: 12, marginBottom: 14 }}
                        onClick={() => setShowAddConv(p => !p)}>
                        + Log Conversation
                      </button>
                      {showAddConv && (
                        <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                            <div className="form-group">
                              <label className="form-label">Date</label>
                              <input className="form-input" type="date" value={convForm.conversation_date}
                                onChange={e => setConvForm(p => ({ ...p, conversation_date: e.target.value }))} />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Contact Type</label>
                              <select className="form-input form-select" value={convForm.contact_type}
                                onChange={e => setConvForm(p => ({ ...p, contact_type: e.target.value }))}>
                                {CONTACT_TYPES.map(ct => <option key={ct.key} value={ct.key}>{ct.label}</option>)}
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="form-label">Spoke With</label>
                              <select className="form-input form-select" value={convForm.spoke_with}
                                onChange={e => setConvForm(p => ({ ...p, spoke_with: e.target.value }))}>
                                {SPOKE_WITH_OPTS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                              </select>
                            </div>
                            <div className="form-group" style={{ gridColumn: 'span 3' }}>
                              <label className="form-label">Summary *</label>
                              <textarea className="form-input" rows={3} style={{ resize: 'vertical' }}
                                value={convForm.summary} onChange={e => setConvForm(p => ({ ...p, summary: e.target.value }))} />
                            </div>
                            <div className="form-group" style={{ gridColumn: 'span 2' }}>
                              <label className="form-label">Next Steps</label>
                              <input className="form-input" placeholder="What happens next?"
                                value={convForm.next_steps} onChange={e => setConvForm(p => ({ ...p, next_steps: e.target.value }))} />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Tone</label>
                              <select className="form-input form-select" value={convForm.sentiment}
                                onChange={e => setConvForm(p => ({ ...p, sentiment: e.target.value }))}>
                                {SENTIMENTS.map(s => <option key={s.key} value={s.key}>{s.emoji} {s.label}</option>)}
                              </select>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                            <button className="btn btn-primary" disabled={!convForm.summary || convSaving} onClick={addConversation}>
                              {convSaving ? 'Saving…' : 'Save'}
                            </button>
                            <button className="btn btn-secondary" onClick={() => setShowAddConv(false)}>Cancel</button>
                          </div>
                        </div>
                      )}
                      {conversations.length === 0 ? (
                        <p style={{ color: 'var(--gray-400)', fontSize: 13, textAlign: 'center', padding: 24 }}>No conversations logged yet.</p>
                      ) : conversations.map(c => (
                        <div key={c.id} className="conv-entry">
                          <div className="conv-entry-header">
                            <span className="conv-date">{formatDate(c.conversation_date)}</span>
                            <span className="conv-type-badge">{CONTACT_TYPES.find(ct => ct.key === c.contact_type)?.label ?? c.contact_type}</span>
                            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{SPOKE_WITH_OPTS.find(o => o.key === c.spoke_with)?.label}</span>
                            {c.sentiment && <span className="conv-sentiment">{sentimentEmoji(c.sentiment)}</span>}
                          </div>
                          <div className="conv-summary">{c.summary}</div>
                          {c.next_steps && <div className="conv-next">Next: {c.next_steps}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Visits tab ── */}
                  {activeTab === 'visits' && (
                    <div>
                      <button className="btn btn-primary" style={{ fontSize: 12, marginBottom: 14 }}
                        onClick={() => setShowAddVisit(p => !p)}>
                        + Log Visit
                      </button>
                      {showAddVisit && (
                        <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                            <div className="form-group">
                              <label className="form-label">Date</label>
                              <input className="form-input" type="date" value={visitForm.visit_date}
                                onChange={e => setVisitForm(p => ({ ...p, visit_date: e.target.value }))} />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Visit Type</label>
                              <select className="form-input form-select" value={visitForm.visit_type}
                                onChange={e => setVisitForm(p => ({ ...p, visit_type: e.target.value }))}>
                                <option value="unofficial">Unofficial</option>
                                <option value="official">Official (counts toward 5)</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label className="form-label">Overall Impressions</label>
                              <select className="form-input form-select" value={visitForm.impressions}
                                onChange={e => setVisitForm(p => ({ ...p, impressions: parseInt(e.target.value) }))}>
                                {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{'★'.repeat(n)} ({n}/5)</option>)}
                              </select>
                            </div>
                            <div className="form-group" style={{ gridColumn: 'span 3' }}>
                              <label className="form-label">Notes / Impressions</label>
                              <textarea className="form-input" rows={3} style={{ resize: 'vertical' }}
                                value={visitForm.notes} onChange={e => setVisitForm(p => ({ ...p, notes: e.target.value }))} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                            <button className="btn btn-primary" disabled={visitSaving} onClick={addVisit}>
                              {visitSaving ? 'Saving…' : 'Save Visit'}
                            </button>
                            <button className="btn btn-secondary" onClick={() => setShowAddVisit(false)}>Cancel</button>
                          </div>
                        </div>
                      )}
                      {visits.length === 0 ? (
                        <p style={{ color: 'var(--gray-400)', fontSize: 13, textAlign: 'center', padding: 24 }}>No visits logged yet.</p>
                      ) : visits.map(v => (
                        <div key={v.id} className="visit-entry">
                          <span className={`visit-type-pill visit-type-pill--${v.visit_type}`}>
                            {v.visit_type === 'official' ? 'Official' : 'Unofficial'}
                          </span>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                              <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>{formatDate(v.visit_date)}</span>
                              {v.impressions && <span className="visit-stars">{'★'.repeat(v.impressions)}{'☆'.repeat(5 - v.impressions)}</span>}
                            </div>
                            {v.notes && <p style={{ fontSize: 13, color: 'var(--gray-600)', margin: 0 }}>{v.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Scholarship / Offer tab ── */}
                  {activeTab === 'scholarship' && (
                    <div>
                      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => setShowOfferForm(p => !p)}>
                          {offer ? 'Edit Offer' : '+ Add Offer'}
                        </button>
                        {offer && (
                          <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setShowReqForm(p => !p)}>
                            + Request Increase
                          </button>
                        )}
                      </div>

                      {showOfferForm && (
                        <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                            <div className="form-group">
                              <label className="form-label">Offer Date</label>
                              <input className="form-input" type="date" value={offerForm.offer_date}
                                onChange={e => setOfferForm(p => ({ ...p, offer_date: e.target.value }))} />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Athletic / yr ($)</label>
                              <input className="form-input" type="number" placeholder="0"
                                value={offerForm.athletic_amount_yr}
                                onChange={e => setOfferForm(p => ({ ...p, athletic_amount_yr: e.target.value }))} />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Academic / yr ($)</label>
                              <input className="form-input" type="number" placeholder="0"
                                value={offerForm.academic_amount_yr}
                                onChange={e => setOfferForm(p => ({ ...p, academic_amount_yr: e.target.value }))} />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                            <button className="btn btn-primary" disabled={offerSaving} onClick={saveOffer}>
                              {offerSaving ? 'Saving…' : 'Save Offer'}
                            </button>
                            <button className="btn btn-secondary" onClick={() => setShowOfferForm(false)}>Cancel</button>
                          </div>
                        </div>
                      )}

                      {offer ? (
                        <>
                          <div className="offer-card">
                            <div style={{ marginBottom: 10, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#15803d', letterSpacing: '0.05em' }}>
                              Scholarship Offer{offer.offer_date ? ` · ${formatDate(offer.offer_date)}` : ''}
                            </div>
                            <div className="offer-amounts">
                              <div className="offer-amount-item">
                                <span className="offer-amount-label">Athletic / yr</span>
                                <span className="offer-amount-value">{formatDollars(offer.athletic_amount_yr)}</span>
                              </div>
                              <div className="offer-amount-item">
                                <span className="offer-amount-label">Academic / yr</span>
                                <span className="offer-amount-value">{formatDollars(offer.academic_amount_yr)}</span>
                              </div>
                              <div className="offer-amount-item">
                                <span className="offer-amount-label">Total / yr</span>
                                <span className="offer-amount-value offer-amount-value--total">
                                  {formatDollars((offer.athletic_amount_yr ?? 0) + (offer.academic_amount_yr ?? 0))}
                                </span>
                              </div>
                              <div className="offer-amount-item">
                                <span className="offer-amount-label">4-Year Est.</span>
                                <span className="offer-amount-value" style={{ color: '#166534' }}>
                                  {formatDollars(((offer.athletic_amount_yr ?? 0) + (offer.academic_amount_yr ?? 0)) * 4)}
                                </span>
                              </div>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <span style={{
                                fontSize: 12, fontWeight: 700, borderRadius: 4, padding: '3px 10px',
                                background: offer.status === 'accepted' ? '#dcfce7' : offer.status === 'declined' ? '#fee2e2' : '#fef3c7',
                                color: offer.status === 'accepted' ? '#15803d' : offer.status === 'declined' ? '#b91c1c' : '#92400e',
                              }}>
                                {offer.status?.charAt(0).toUpperCase() + offer.status?.slice(1)}
                              </span>
                            </div>
                          </div>

                          {showReqForm && (
                            <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginTop: 12 }}>
                              <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: 'var(--navy)' }}>Request Scholarship Increase</p>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                <div className="form-group">
                                  <label className="form-label">Request Date</label>
                                  <input className="form-input" type="date" value={reqForm.request_date}
                                    onChange={e => setReqForm(p => ({ ...p, request_date: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Athletic Increase ($)</label>
                                  <input className="form-input" type="number" placeholder="0"
                                    value={reqForm.requested_athletic_increase}
                                    onChange={e => setReqForm(p => ({ ...p, requested_athletic_increase: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                  <label className="form-label">Academic Increase ($)</label>
                                  <input className="form-input" type="number" placeholder="0"
                                    value={reqForm.requested_academic_increase}
                                    onChange={e => setReqForm(p => ({ ...p, requested_academic_increase: e.target.value }))} />
                                </div>
                                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                                  <label className="form-label">Notes</label>
                                  <input className="form-input" placeholder="Reason, competing offers, etc."
                                    value={reqForm.notes} onChange={e => setReqForm(p => ({ ...p, notes: e.target.value }))} />
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                                <button className="btn btn-primary" disabled={reqSaving} onClick={addOfferRequest}>
                                  {reqSaving ? 'Submitting…' : 'Submit Request'}
                                </button>
                                <button className="btn btn-secondary" onClick={() => setShowReqForm(false)}>Cancel</button>
                              </div>
                            </div>
                          )}

                          {offerRequests.length > 0 && (
                            <div style={{ marginTop: 20 }}>
                              <p style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 10 }}>Increase Requests</p>
                              {offerRequests.map(req => (
                                <div key={req.id} className="offer-request-entry">
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>{formatDate(req.request_date)}</span>
                                    <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                                      Athletic +{formatDollars(req.requested_athletic_increase)} · Academic +{formatDollars(req.requested_academic_increase)}
                                    </span>
                                    <span className={`offer-request-response offer-request-response--${req.school_response}`}>
                                      {req.school_response.charAt(0).toUpperCase() + req.school_response.slice(1)}
                                    </span>
                                  </div>
                                  {req.notes && <p style={{ fontSize: 12, color: 'var(--gray-500)', margin: '6px 0 4px' }}>{req.notes}</p>}
                                  {req.school_response === 'approved' && (
                                    <p style={{ fontSize: 12, color: '#15803d', margin: '4px 0 0' }}>
                                      New: {formatDollars(req.new_athletic_amount)}/yr athletic · {formatDollars(req.new_academic_amount)}/yr academic
                                    </p>
                                  )}
                                  {req.school_response === 'pending' && (
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                      <button className="btn btn-secondary" style={{ fontSize: 11, color: '#15803d', borderColor: '#bbf7d0' }}
                                        onClick={() => {
                                          const newAth = prompt('New athletic amount/yr ($):');
                                          const newAcd = prompt('New academic amount/yr ($):');
                                          if (newAth !== null) updateRequestResponse(req.id, 'approved', newAth, newAcd);
                                        }}>
                                        Mark Approved
                                      </button>
                                      <button className="btn btn-secondary" style={{ fontSize: 11, color: '#b91c1c', borderColor: '#fecaca' }}
                                        onClick={() => updateRequestResponse(req.id, 'denied', null, null)}>
                                        Mark Denied
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <p style={{ color: 'var(--gray-400)', fontSize: 13, textAlign: 'center', padding: 32 }}>No offer added yet.</p>
                      )}
                    </div>
                  )}

                  {/* ── Grade Scorecard tab ── */}
                  {activeTab === 'grade' && (
                    <div>
                      <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 16 }}>
                        Grade this program across key dimensions. These grades are private and reflect your assessment of fit — they are <strong>not</strong> shared with the school.
                      </p>
                      <div className="rec-scorecard">
                        {GRADE_CATEGORIES.map(cat => (
                          <div key={cat.key} className="rec-scorecard-row">
                            <span className="rec-scorecard-label">{cat.label}</span>
                            <select
                              className="rec-grade-select"
                              value={grades[cat.key] ?? ''}
                              onChange={e => setGrades(p => ({ ...p, [cat.key]: e.target.value }))}
                            >
                              <option value="">—</option>
                              {RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </div>
                        ))}
                        <div className="rec-overall-grade">
                          <span className="rec-overall-grade-label">Overall Program Grade</span>
                          <span className="rec-overall-grade-value">{overallGrade ?? '—'}</span>
                        </div>
                      </div>
                      <div className="form-group" style={{ marginTop: 14 }}>
                        <label className="form-label">Notes on This Program</label>
                        <textarea className="form-input" rows={4} style={{ resize: 'vertical' }}
                          placeholder="e.g. Strong coaching staff, good academic support, competitive conference..."
                          value={gradeNotes} onChange={e => setGradeNotes(e.target.value)} />
                      </div>
                      <button className="btn btn-primary" style={{ marginTop: 10 }} disabled={gradeSaving} onClick={saveGrades}>
                        {gradeSaving ? 'Saving…' : 'Save Grade'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
